import { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, Siren, Zap, BadgeCheck, Lock, FileSearch, PhoneCall, GraduationCap, ShieldCheck, Wrench, Receipt, Headset, Wallet, Check, X, Timer,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { SectionHead } from "@/components/home/HomeSections";
import { iconFor, FAQ } from "@/lib/data";
import { imgFor, BLOGS } from "@/lib/content";

const BANNERS = [
  ["ac", "AC Service & Repair", "Beat the heat — expert AC repair at your doorstep.", "/services/ac-repair"],
  ["cleaner", "Deep Home Cleaning", "Spotless homes by trained cleaning professionals.", "/services/home-cleaning"],
  ["electrician", "Expert Electricians", "Switches, fans, wiring and MCB — fixed safely.", "/services/electrician"],
  ["happy-family", "Happy Homes, Happy Families", "Trusted professionals, transparent pricing, warranty-backed work.", "/book"],
];

export const BannerCarousel = () => (
  <section className="container-x pt-4" data-testid="banner-carousel">
    <Carousel opts={{ loop: true }}>
      <CarouselContent>
        {BANNERS.map(([img, t, s, to], i) => (
          <CarouselItem key={t}>
            <div className="relative h-[300px] sm:h-[420px] rounded-[2rem] overflow-hidden">
              <img src={`/img/${img}.webp`} alt={t} loading={i ? "lazy" : "eager"} className="absolute inset-0 h-full w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-r from-[#08140E]/85 via-[#08140E]/40 to-transparent" />
              <div className="relative h-full flex flex-col justify-center p-8 sm:p-14 max-w-xl text-white">
                <span className="eyebrow text-emerald-300">Onkaal.com</span>
                <h2 className="text-3xl sm:text-5xl font-extrabold mt-2 tracking-tight">{t}</h2>
                <p className="mt-3 text-white/85">{s}</p>
                <Button asChild className="mt-6 w-fit rounded-full h-11 px-6 bg-emerald-500 hover:bg-emerald-400 text-[#08140E] font-semibold" data-testid={`banner-cta-${i}`}><Link to={to}>Book Now <ArrowRight className="h-4 w-4 ml-1" /></Link></Button>
              </div>
            </div>
          </CarouselItem>
        ))}
      </CarouselContent>
      <CarouselPrevious className="left-4" data-testid="banner-prev" />
      <CarouselNext className="right-4" data-testid="banner-next" />
    </Carousel>
    <p className="text-[11px] text-muted-foreground mt-2">Same-day visits subject to technician availability in your area.</p>
  </section>
);

const FEATURED = ["ac-repair", "electrician", "plumber", "carpenter", "home-cleaning", "mobile-repair", "laptop-repair", "cctv-installation", "bike-service"];

export const FeaturedServices = ({ services }) => {
  const list = FEATURED.map((s) => services.find((x) => x.slug === s)).filter(Boolean);
  return (
    <section className="container-x section-y" data-testid="featured-services">
      <SectionHead eyebrow="Featured Services" title="Most booked by our customers" />
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {list.map((s, i) => {
          const I = iconFor(s.slug);
          return (
            <Link key={s.slug} to={`/services/${s.slug}`} className={`group relative overflow-hidden rounded-3xl h-64 ${i === 0 ? "lg:col-span-2 lg:h-auto lg:row-span-2" : ""}`} data-testid={`featured-${s.slug}`}>
              <img src={imgFor(s.slug)} alt={`${s.name} professional at work`} loading="lazy" className="absolute inset-0 h-full w-full object-cover group-hover:scale-105 transition-transform duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#08140E]/90 via-[#08140E]/20 to-transparent" />
              <div className="absolute bottom-0 p-5 text-white flex items-end justify-between w-full">
                <div><span className="h-10 w-10 rounded-xl bg-white/15 backdrop-blur grid place-items-center mb-3"><I className="h-5 w-5" /></span><p className="font-display text-xl font-bold">{s.name}</p><p className="text-xs text-white/75">Inspection ₹199 · Same day</p></div>
                <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>
          );
        })}
      </div>
    </section>
  );
};

export const EmergencySameDay = () => (
  <section className="container-x grid md:grid-cols-2 gap-4" data-testid="emergency-sameday">
    <div className="rounded-3xl p-8 bg-rose-50 dark:bg-rose-950/30 border border-rose-200 dark:border-rose-900" data-testid="emergency-services">
      <Siren className="h-8 w-8 text-rose-600" />
      <h3 className="text-2xl font-bold mt-4">Emergency Services</h3>
      <p className="text-sm text-muted-foreground mt-2">Power failure, water leakage, blocked drains or a broken lock? Call us and we'll arrange the nearest available professional.</p>
      <div className="flex flex-wrap gap-2 mt-5">{["Electrician", "Plumber", "Carpenter", "AC Repair"].map((t) => <span key={t} className="rounded-full bg-white/70 dark:bg-white/10 px-3 py-1 text-xs font-semibold">{t}</span>)}</div>
      <Button asChild className="mt-6 rounded-full bg-rose-600 hover:bg-rose-700" data-testid="emergency-call-button"><a href="tel:+919986019922"><PhoneCall className="h-4 w-4 mr-2" />Call +91 9986019922</a></Button>
    </div>
    <div className="rounded-3xl p-8 bg-secondary border border-border" data-testid="same-day-service">
      <Timer className="h-8 w-8 text-primary" />
      <h3 className="text-2xl font-bold mt-4">Same Day Service</h3>
      <p className="text-sm text-muted-foreground mt-2">Book before noon and get a visit the same day in most serviceable areas — no waiting for days.</p>
      <ul className="mt-5 space-y-2 text-sm">{["Instant booking confirmation", "Pick your time slot", "Track your booking live"].map((t) => <li key={t} className="flex gap-2"><Check className="h-4 w-4 text-primary" />{t}</li>)}</ul>
      <Button asChild className="mt-6 rounded-full" data-testid="same-day-book-button"><Link to="/book"><Zap className="h-4 w-4 mr-2" />Book for Today</Link></Button>
    </div>
  </section>
);

export const BeforeAfter = () => {
  const [pos, setPos] = useState(50);
  return (
    <section className="container-x section-y grid lg:grid-cols-2 gap-10 items-center" data-testid="before-after-section">
      <div>
        <span className="eyebrow">Before & After</span>
        <h2 className="h2 mt-2">See the Onkaal difference</h2>
        <p className="mt-3 text-muted-foreground">Our bathroom deep cleaning removes hard-water stains, grime and mildew — drag the slider to compare.</p>
        <Button asChild className="mt-6 rounded-full" data-testid="before-after-book"><Link to="/services/bathroom-cleaning">Book Bathroom Cleaning</Link></Button>
        <p className="text-[11px] text-muted-foreground mt-3">Illustrative images.</p>
      </div>
      <div className="relative aspect-square rounded-3xl overflow-hidden select-none" data-testid="before-after-slider">
        <img src="/img/bathroom-after.webp" alt="Bathroom after deep cleaning" loading="lazy" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 overflow-hidden" style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}>
          <img src="/img/bathroom-before.webp" alt="Bathroom before deep cleaning" loading="lazy" className="absolute inset-0 h-full w-full object-cover" />
        </div>
        <div className="absolute inset-y-0 w-1 bg-white shadow-xl" style={{ left: `${pos}%` }} />
        <span className="absolute top-4 left-4 rounded-full bg-black/60 text-white text-xs px-3 py-1">Before</span>
        <span className="absolute top-4 right-4 rounded-full bg-primary text-white text-xs px-3 py-1">After</span>
        <input type="range" min={0} max={100} value={pos} onChange={(e) => setPos(+e.target.value)} aria-label="Compare before and after" className="absolute inset-0 w-full h-full opacity-0 cursor-ew-resize" data-testid="before-after-range" />
      </div>
    </section>
  );
};

const VERIFY = [[FileSearch, "Document check", "Government ID and address proof verified."], [BadgeCheck, "Background verification", "Identity and background checks before onboarding."], [GraduationCap, "Skill assessment", "Experience and skill certificates reviewed."], [ShieldCheck, "Ongoing ratings", "Every job is rated; low performers are removed."]];

export const HowWeVerify = () => (
  <section className="bg-secondary/50 border-y border-border" data-testid="how-we-verify">
    <div className="container-x section-y grid lg:grid-cols-[1fr_1.2fr] gap-10 items-center">
      <img src="/img/partner-team-v2.webp" alt="Verified Onkaal service professionals" loading="lazy" className="rounded-3xl w-full aspect-[3/2] object-cover" />
      <div>
        <SectionHead eyebrow="How we verify professionals" title="Only verified experts enter your home" />
        <div className="grid sm:grid-cols-2 gap-4">
          {VERIFY.map(([I, t, d], i) => <div key={t} className="card-soft p-5" data-testid={`verify-step-${i}`}><I className="h-6 w-6 text-primary" /><p className="font-semibold mt-3">{t}</p><p className="text-sm text-muted-foreground">{d}</p></div>)}
        </div>
      </div>
    </div>
  </section>
);

const BADGES = [[BadgeCheck, "Verified Professionals"], [ShieldCheck, "Background Verified"], [Wallet, "Pay After Service — Cash / UPI / Card"], [Receipt, "Digital Invoice"], [Wrench, "Genuine Spare Parts"], [Headset, "Customer Support 7 Days"], [Lock, "Secure Booking"], [ShieldCheck, "Up to 180 Days Warranty"]];

export const TrustBadges = () => (
  <section className="container-x py-10" data-testid="trust-badges">
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      {BADGES.map(([I, t], i) => <div key={t} className="card-soft p-4 flex flex-col items-center text-center gap-2" data-testid={`trust-badge-${i}`}><span className="h-11 w-11 rounded-full bg-secondary grid place-items-center"><I className="h-5 w-5 text-primary" /></span><p className="text-sm font-semibold">{t}</p></div>)}
    </div>
  </section>
);

export const ServiceGuarantee = () => (
  <section className="container-x section-y" data-testid="service-guarantee">
    <div className="rounded-[2rem] border-2 border-primary/30 bg-card p-8 sm:p-12 grid md:grid-cols-[auto_1fr] gap-8 items-center">
      <ShieldCheck className="h-24 w-24 text-primary" />
      <div>
        <h2 className="h2">The Onkaal Service Guarantee</h2>
        <p className="mt-3 text-muted-foreground max-w-2xl">If the same issue comes back within your warranty period, we'll send a professional to fix it again at no extra service charge. Up to 180 days on most repairs.</p>
        <Link to="/warranty" className="inline-flex mt-4 text-primary font-semibold" data-testid="guarantee-warranty-link">See warranty by service →</Link>
      </div>
    </div>
  </section>
);

const VS = [["Verified, background-checked professionals", true, false], ["Transparent ₹199 inspection & upfront quote", true, false], ["Pay after service", true, true], ["Digital invoice", true, false], ["Up to 180 days warranty", true, false], ["Online booking & tracking", true, false]];

export const VsOthers = () => (
  <section className="container-x pb-16" data-testid="vs-others">
    <SectionHead eyebrow="Why Onkaal vs others" title="A better way to get things fixed" />
    <div className="card-soft overflow-hidden">
      <div className="grid grid-cols-[1fr_100px_100px] sm:grid-cols-[1fr_160px_160px] bg-muted/60 text-sm font-semibold px-5 py-3"><span>Feature</span><span className="text-center text-primary">Onkaal</span><span className="text-center">Local / Unverified</span></div>
      {VS.map(([t, a, b]) => (
        <div key={t} className="grid grid-cols-[1fr_100px_100px] sm:grid-cols-[1fr_160px_160px] px-5 py-3 border-t border-border text-sm">
          <span>{t}</span>
          <span className="grid place-items-center">{a ? <Check className="h-5 w-5 text-primary" /> : <X className="h-5 w-5 text-rose-500" />}</span>
          <span className="grid place-items-center">{b ? <Check className="h-5 w-5 text-muted-foreground" /> : <X className="h-5 w-5 text-rose-500" />}</span>
        </div>
      ))}
    </div>
  </section>
);

export const HomeFaq = () => (
  <section className="container-x pb-16" data-testid="home-faq">
    <SectionHead eyebrow="FAQs" title="Frequently asked questions" action={<Link to="/help" className="text-sm font-semibold text-primary">Help Center →</Link>} />
    <Accordion type="single" collapsible className="card-soft px-6">
      {FAQ.slice(0, 6).map((f, i) => (
        <AccordionItem key={f.q} value={f.q} className={i === 5 ? "border-b-0" : ""}>
          <AccordionTrigger className="text-left font-semibold hover:no-underline" data-testid={`home-faq-${i}`}>{f.q}</AccordionTrigger>
          <AccordionContent className="text-muted-foreground">{f.a}</AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  </section>
);

export const BlogCards = ({ list = BLOGS }) => (
  <div className="grid md:grid-cols-3 gap-4">
    {list.map((b) => (
      <Link key={b.slug} to={`/blogs/${b.slug}`} className="card-soft lift overflow-hidden" data-testid={`blog-card-${b.slug}`}>
        <img src={`/img/${b.img}.webp`} alt={b.title} loading="lazy" className="h-44 w-full object-cover" />
        <div className="p-5"><p className="text-xs text-muted-foreground">{b.date}</p><h3 className="font-semibold mt-1">{b.title}</h3><p className="text-sm text-muted-foreground mt-2">{b.excerpt}</p></div>
      </Link>
    ))}
  </div>
);

export const LatestBlogs = () => (
  <section className="container-x pb-16" data-testid="latest-blogs">
    <SectionHead eyebrow="Latest Blogs" title="Tips for a better home" action={<Link to="/blogs" className="text-sm font-semibold text-primary">All articles →</Link>} />
    <BlogCards />
  </section>
);
